import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import PropTypes from "prop-types";
import Control from "../molecules/Control";
import { useBoundStore } from "../../stores/useBoundStore";
import { useMeasure } from "react-use";
import { colorSchemeThemes } from "../../utils/styling";

const Holder = styled.div`
  display: grid;
  overflow: hidden;
  width: 100%;
  height: 100%;
  grid-template-columns: ${(props) => props.xCols};
  grid-template-rows: ${(props) => props.yRows};
  background-color: var(--backgroundColor);
  align-items: start;
  justify-items: start;

  opacity: var(--opacity);

  transition: opacity 0.3s ease-in-out;
  cursor: pointer;

  &:hover {
    opacity: ${({ active }) => (active ? 0.5 : 0)};
  }

  &.mirror {
    .control {
      &:nth-child(2),
      &:nth-child(4) {
        transform: scaleX(-1);
      }
    }
  }

  &.mirrorTwo {
    .control {
      &:nth-child(2) {
        transform: scaleX(-1);
      }

      &:nth-child(3) {
        transform: scaleY(-1);
      }

      &:nth-child(4) {
        transform: scaleX(-1) scaleY(-1);
      }
    }
  }
`;

function RepeatedControl(props) {
  const hiddenRepetitions = useBoundStore((state) => state.hiddenRepetitions);
  const setHiddenRepetitions = useBoundStore((state) => state.setHiddenRepetitions);
  const repetition = useBoundStore((state) => state.repetition);
  const [ref, { width, height }] = useMeasure();
  const [xCols, setXCols] = useState("50% 50%");
  const [yRows, setYRows] = useState("50% 50%");
  const theme = useTheme();
  const [colors, setColors] = useState(theme.colors);

  const isHidden = () => {
    return hiddenRepetitions.length && hiddenRepetitions.filter(r => r === props.id).length > 0;
  }

  const handleOnClick = () => {
    if (!props.canvas) return;

    if (isHidden()) {
      setHiddenRepetitions(hiddenRepetitions.filter(r => r !== props.id));
    } else {
      setHiddenRepetitions([...hiddenRepetitions, props.id]);
    }
  };

  useEffect(() => {
    if (props.customSettings) {
      setColors(colorSchemeThemes[props.customSettings.colorScheme].colors);
    } else {
      setColors(theme.colors);
    }
  }, [props.customSettings, theme.colors]);

  useEffect(() => {
    let colWidths = [];
    if (width % 2 === 0) {
      colWidths = [width / 2, width / 2];
    } else {
      colWidths = [Math.floor(width / 2), Math.ceil(width / 2)];
    }
    setXCols(colWidths.join("px ") + "px");
  }, [width, setXCols]);

  useEffect(() => {
    let rowHeights = [];
    if (height % 2 === 0) {
      rowHeights = [height / 2, height / 2];
    } else {
      rowHeights = [Math.floor(height / 2), Math.ceil(height / 2)];
    }
    setYRows(rowHeights.join("px ") + "px");
  }, [height, setYRows]);

  return (
    <Holder
      ref={ref}
      xCols={xCols}
      yRows={yRows}
      style={{
        "--backgroundColor":
          props.customSettings && colors[props.customSettings.background],
        "--opacity": isHidden() ? 0 : 1,
      }}
      active={!isHidden()}
      className={props.customSettings?.repetition || props.repeat || repetition}
      onClick={handleOnClick}
    >
      <Control customSettings={props.customSettings} />
      <Control customSettings={props.customSettings} />
      <Control customSettings={props.customSettings} />
      <Control customSettings={props.customSettings} />
    </Holder>
  );
}

RepeatedControl.propTypes = {
  repeat: PropTypes.string,
  customSettings: PropTypes.object,
};

export default RepeatedControl;
